import React from 'react';
import { useStudioTheme } from './studioTheme';

export function StudioShell({
  sidebar,
  main,
  right,
  accentColor = '#7c3aed',
}: {
  sidebar:      React.ReactNode;
  main:         React.ReactNode;
  right:        React.ReactNode;
  accentColor?: string;
}) {
  const { ST } = useStudioTheme();

  return (
    <div style={{
      position:            'fixed',
      inset:               0,
      display:             'grid',
      gridTemplateColumns: '220px 1fr 300px',
      gridTemplateRows:    '100vh',
      backgroundColor:     ST.bg.base,
      overflow:            'hidden',
      zIndex:              9999,
    }}>

      {/* ── Animated ambient background ─────────────────────────────── */}
      <div style={{
        position:      'fixed',
        inset:         0,
        pointerEvents: 'none',
        zIndex:        0,
        overflow:      'hidden',
      }}>
        {/* Primary purple/indigo ambient — always present */}
        <div
          className="studio-ambient-a"
          style={{
            position:        'absolute',
            top:             '-20%',
            left:            '-10%',
            width:           '65%',
            height:          '70%',
            borderRadius:    '50%',
            background:      'radial-gradient(ellipse, rgba(124,58,237,0.20) 0%, rgba(99,102,241,0.09) 40%, transparent 70%)',
          }}
        />
        {/* Secondary — workspace accent color, changes with selection */}
        <div
          className="studio-ambient-b"
          style={{
            position:        'absolute',
            top:             '20%',
            right:           '-15%',
            width:           '55%',
            height:          '60%',
            borderRadius:    '50%',
            background:      `radial-gradient(ellipse, ${accentColor}14 0%, ${accentColor}06 45%, transparent 70%)`,
            transition:      'background 1.2s ease',
          }}
        />
        {/* Bottom vignette glow */}
        <div style={{
          position:   'absolute',
          bottom:     '-5%',
          left:       '20%',
          width:      '60%',
          height:     '35%',
          borderRadius: '50%',
          background: `radial-gradient(ellipse, ${accentColor}06 0%, transparent 65%)`,
          transition: 'background 1.2s ease',
        }} />
        {/* Bottom-left secondary accent */}
        <div style={{
          position:      'absolute',
          bottom:        '-20%',
          left:          '-5%',
          width:         '45%',
          height:        '50%',
          borderRadius:  '50%',
          background:    `radial-gradient(ellipse, ${accentColor}05 0%, transparent 60%)`,
          transition:    'background 1.4s ease',
          pointerEvents: 'none',
        }} />
        {/* Center soft indigo depth bloom */}
        <div style={{
          position:      'absolute',
          top:           '20%',
          left:          '25%',
          width:         '50%',
          height:        '55%',
          borderRadius:  '50%',
          background:    'radial-gradient(ellipse, rgba(99,102,241,0.04) 0%, transparent 60%)',
          pointerEvents: 'none',
        }} />
        {/* Edge vignette — darkens corners for depth */}
        <div style={{
          position:      'absolute',
          inset:         0,
          background:    'radial-gradient(ellipse 88% 88% at 50% 50%, transparent 30%, rgba(0,0,0,0.22) 100%)',
          pointerEvents: 'none',
        }} />
        {/* Subtle noise texture overlay */}
        <div style={{
          position:   'absolute',
          inset:      0,
          opacity:    0.024,
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
          backgroundRepeat: 'repeat',
          backgroundSize:   '180px 180px',
        }} />
      </div>

      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      <div style={{
        position:        'relative',
        zIndex:          1,
        backgroundImage: ST.gradient.sidebar,
        backdropFilter:  `blur(${ST.glass.blur})`,
        WebkitBackdropFilter: `blur(${ST.glass.blur})`,
        borderRight:     `1px solid ${ST.border.subtle}`,
        overflowY:       'auto',
        overflowX:       'hidden',
        display:         'flex',
        flexDirection:   'column',
        height:          '100vh',
      }}>
        {sidebar}
      </div>

      {/* ── Main content ─────────────────────────────────────────────── */}
      <div
        id="studio-main-scroll"
        style={{
          position:        'relative',
          zIndex:          1,
          backgroundColor: 'transparent',
          overflowY:       'auto',
          overflowX:       'hidden',
          height:          '100vh',
        }}
      >
        {main}
      </div>

      {/* ── Right panel ─────────────────────────────────────────────── */}
      <div style={{
        position:        'relative',
        zIndex:          1,
        backgroundImage: ST.gradient.sidebar,
        backdropFilter:  `blur(${ST.glass.blur})`,
        WebkitBackdropFilter: `blur(${ST.glass.blur})`,
        borderLeft:      `1px solid ${ST.border.subtle}`,
        overflowY:       'auto',
        overflowX:       'hidden',
        height:          '100vh',
      }}>
        {right}
      </div>
    </div>
  );
}
